/**
 * The two ways a CV upload can fail with nothing to tell the person, and what to tell them.
 *
 * A refusal from the server (`CV_REFUSALS`) comes with an `error_code`. These two do not: either
 * the request never reached the server, or the file never left the phone. Until 01/10/2026 both
 * ended in a catch block that recognised neither, so the "Continua" button stopped spinning and
 * nothing else happened, and the person tried again until they gave up.
 *
 * The codes are client-side only and never sent anywhere. They share the `error_code` slot of the
 * errors atom so that `UploadedCVUI` shows them through the same lookup as a server refusal.
 */
export const CV_UPLOAD_NETWORK_FAILURE_CODE = 'cv_upload_network_failure';

export const CV_UPLOAD_FAILURES: Record<string, { messageKey: string; testId: string }> = {
  [CV_UPLOAD_NETWORK_FAILURE_CODE]: {
    messageKey: 'we_could_not_send_your_cv,check_your_connection_and_try_again',
    testId: 'upload-cv-network-failure-error',
  },
  // The signup boxes refused by the backend (RW-422): the page holds texts older than the ones in
  // force, or the terms box did not arrive ticked.
  signup_texts_outdated: {
    messageKey: 'common:terms_and_conditions.texts_outdated',
    testId: 'upload-cv-signup-texts-outdated-error',
  },
  signup_terms_not_accepted: {
    messageKey: 'accept_the_terms_and_conditions_to_continue',
    testId: 'upload-cv-signup-terms-not-accepted-error',
  },
};

/**
 * True when the upload failed without any answer from the server.
 *
 * Our axios interceptor turns every error that carries a response into `{ code: <status>, data }`
 * and lets the others through untouched, as an `AxiosError` with no `response`: a request the
 * browser aborted (status 0 in RUM), a dropped connection, or a request the server closed
 * without answering, which is how the 60-second hangs of 22-29/09/2026 reached the browser.
 */
export function isUploadNetworkFailure(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;

  const error = err as { isAxiosError?: boolean; response?: unknown; data?: unknown; code?: unknown };

  return error.isAxiosError === true && !error.response && !error.data;
}
